import React, { useEffect, useRef } from 'react';
import { NostrEvent, NostrFilter, NPool, NRelay1 } from '@nostrify/nostrify';
import { NostrContext } from '@nostrify/react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '@/hooks/useAppContext';

interface NostrProviderProps {
  children: React.ReactNode;
}

const GIT_KINDS = [
  30617, // NIP-34 Repository Announcement
  30618, // NIP-34 Commit Announcement
  1617, // NIP-34 Patch
  1621, // NIP-34 Issue
];

// App showcase related kinds
const APP_KINDS = [
  31733, // App submissions
  30267, // NIP-51 Lists (featured/approved apps)
  1984,  // NIP-56 Reports (hidden apps)
];

// Default git relays that should always be connected regardless of user configuration
const DEFAULT_GIT_RELAYS = [
  'wss://relay.ngit.dev/',
  'wss://git.shakespeare.diy/',
];

// Additional relays for app showcase queries (to ensure we can fetch templates even if main relays fail)
// Using only the fastest and most reliable relays for app submissions
const DEFAULT_APP_RELAYS = [
  'wss://relay.ditto.pub/',
  'wss://relay.nostr.band/',
];

const NostrProvider: React.FC<NostrProviderProps> = (props) => {
  const { children } = props;
  const { config } = useAppContext();

  const queryClient = useQueryClient();

  // Create NPool instance only once
  const pool = useRef<NPool | undefined>(undefined);

  // Use refs so the pool always has the latest data
  const relayUrls = useRef<string[]>(config.relayUrls);
  const gitRelays = useRef<string[]>(DEFAULT_GIT_RELAYS);
  const appRelays = useRef<string[]>(DEFAULT_APP_RELAYS);

  // Update refs when config changes
  useEffect(() => {
    relayUrls.current = config.relayUrls;
    // Update git relays from config, falling back to defaults
    gitRelays.current = config.ngitServers.length > 0
      ? config.ngitServers.map(server => `wss://${server}/`)
      : DEFAULT_GIT_RELAYS;
    queryClient.invalidateQueries({ queryKey: ['nostr'] });
  }, [config.relayUrls, config.ngitServers, queryClient]);

  // Initialize NPool only once
  if (!pool.current) {
    pool.current = new NPool({
      open(url: string) {
        return new NRelay1(url);
      },
      reqRouter(filters) {
        const routes = new Map<string, NostrFilter[]>();

        // Route to all configured relays
        for (const url of relayUrls.current) {
          routes.set(url, filters);
        }

        // Check if this query includes app-related kinds
        const hasAppKinds = filters.some(filter =>
          filter.kinds?.some(kind => APP_KINDS.includes(kind))
        );

        // Always route to git relays to ensure we can fetch git-related data
        // and profile metadata for git users even when logged out
        for (const url of gitRelays.current) {
          routes.set(url, filters);
        }

        // If querying for app-related events, also route to default app relays
        // This ensures we can fetch templates even if user's configured relays fail
        if (hasAppKinds) {
          for (const url of appRelays.current) {
            routes.set(url, filters);
          }
        }

        return routes;
      },
      eventRouter(event: NostrEvent) {
        // Publish to all configured relays
        const allRelays = new Set<string>(relayUrls.current);

        // If it's a git-related event, also publish to the git relays
        if (GIT_KINDS.includes(event.kind)) {
          for (const url of gitRelays.current) {
            allRelays.add(url);
          }
        }

        // If it's an app-related event, also publish to app relays
        if (APP_KINDS.includes(event.kind)) {
          for (const url of appRelays.current) {
            allRelays.add(url);
          }
        }

        return [...allRelays];
      },
    });
  }

  return (
    <NostrContext.Provider value={{ nostr: pool.current }}>
      {children}
    </NostrContext.Provider>
  );
};

export default NostrProvider;